import { ContentLayout } from '@/bundle/Layouts/ContentLayout/ContentLayout';
import { PageBody } from '@/components/Page/PageBody/PageBody';
import { Box } from '@/components/Box/Box';
import { PlanLimitsDetailsWidget } from '@/bundle/_MasterAdmin/_BillingPage/ui/PlanLimitsDetailsWidget/PlanLimitsDetailsWidget';
import { PageColumn } from '@/components/Page/PageColumn/PageColumn';
import { PlanDetailsWidget } from '@/bundle/_MasterAdmin/_BillingPage/ui/PlanDetailsWidget/PlanDetailsWidget';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getCustomerPortalLink, getInternalOrganizationBillingDetails, retrySubscriptionPayment } from './api/api';
import { StripeInfoBanner } from '@/bundle/shared/components/banner/StripeInfoBanner/StripeInfoBanner';
import { showSnackbarResponseError } from '@/helpers/helpers';
import { Button } from '@/components/Button/Button';
import { useState } from 'react';
import { CancelSubscriptionModal } from '@/bundle/_MasterAdmin/_BillingPage/ui/CancelSubscriptionModal';
import { SubscriptionInvoices } from './ui/BIllingInvoices/SubscriptionInvoices';
import { SubscriptionNotifications } from './ui/SubscriptionNotifications/SubscriptionNotifications';
import { useSubscriptionStatus } from '@/bundle/shared/hooks/useSubscriptionStatus';
import { showSnackbar } from '@/components/Snackbar/Snackbar';
import { SubscriptionPaymentFailedSnackbarMessage, SubscriptionPaymentSuccessSnackbarMessage } from './ui/const';
import { useUser } from '@/context/userContext';
import { useNavigate } from 'react-router';
import { getSelectBillingPlanPath } from '@/bundle/Auth/AdminOnboardingFlow/SelectBillingPlanPage/path';

export const BillingPage = () => {
  const navigate = useNavigate();
  const { refetchUser } = useUser();
  const queryClient = useQueryClient();
  const { isCancelledSubscription, isPendingCancellationSubscription } = useSubscriptionStatus();
  const [openModal, setOpenModal] = useState(false);

  const { data: billingDetailsData, isPending } = useQuery({
    queryKey: ['get_internal_organization_billing_details'],
    queryFn: () => getInternalOrganizationBillingDetails(),
  });

  const { mutate: getCustomerPortalLinkMutate, isPending: isPendingCustomerPortalLink } = useMutation({
    mutationKey: ['get_customer_portal_link'],
    mutationFn() {
      return getCustomerPortalLink();
    },
    onSuccess(response) {
      if (response?.error) {
        return showSnackbarResponseError(response?.error);
      }

      return window.open(response?.body?.customer_portal_url, '_blank');
    },
  });

  const { mutate: retrySubscriptionPaymentMutate, isPending: isPendingRetrySubscriptionPayment } = useMutation({
    mutationKey: ['retry_subscription_payment'],
    mutationFn() {
      return retrySubscriptionPayment();
    },
    onSuccess(response) {
      if (response?.error) {
        return showSnackbar(<SubscriptionPaymentFailedSnackbarMessage />, {
          variant: 'error',
          maxWidth: '350px',
        });
      }

      queryClient.invalidateQueries({ queryKey: ['get_internal_organization_billing_details'] });
      refetchUser();
      showSnackbar(<SubscriptionPaymentSuccessSnackbarMessage />, { maxWidth: '350px' });
    },
  });

  const navigateToCustomerPortal = () => {
    if (isPendingCustomerPortalLink) return;

    getCustomerPortalLinkMutate();
  };

  const navigateToSelectBillingPlan = () => {
    navigate(getSelectBillingPlanPath());
  };

  const billingDetails = billingDetailsData?.body;
  const showPlanLimitsWidget = billingDetails && !isCancelledSubscription;
  const subscriptionExpirationDate = billingDetails?.subscription?.current_period_end;
  const canCancelSubscription = !isPending && !isPendingCancellationSubscription && !isCancelledSubscription;

  return (
    <>
      <ContentLayout
        title='Billing'
        breadcrumbs='Billing'
        banner={<StripeInfoBanner />}
        headerAction={
          canCancelSubscription && (
            <Button variant='secondary' onClick={() => setOpenModal(true)}>
              Cancel subscription
            </Button>
          )
        }
      >
        <PageBody>
          <Box display='flex' flexDirection='column' rowGap='48px'>
            <Box width='100%' display='flex' gap='24px' mobile={{ flexDirection: 'column' }}>
              <PageColumn>
                {billingDetails && (
                  <PlanDetailsWidget
                    billingDetails={billingDetails}
                    onCustomerPortalNavigate={navigateToCustomerPortal}
                  />
                )}
              </PageColumn>
              <PageColumn>
                {showPlanLimitsWidget && <PlanLimitsDetailsWidget billingDetails={billingDetails} />}
              </PageColumn>
            </Box>
            <SubscriptionNotifications
              onRetryPayment={retrySubscriptionPaymentMutate}
              onReactiveSubscription={navigateToSelectBillingPlan}
              onCustomerPortalNavigate={navigateToCustomerPortal}
              subscriptionExpirationDate={subscriptionExpirationDate}
              isLoading={isPendingRetrySubscriptionPayment}
            />
            <SubscriptionInvoices onCustomerPortalNavigate={navigateToCustomerPortal} />
          </Box>
        </PageBody>
      </ContentLayout>
      <CancelSubscriptionModal isOpen={openModal} onClose={() => setOpenModal(false)} />
    </>
  );
};
